import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileCsv } from "@fortawesome/free-solid-svg-icons";
import { notify } from "react-notify-toast";
import { getStudents } from "../../../services/studentsService";
import {
  DEFAULT_CSV_FIELDS,
  STUDENT_CSV_FIELDS,
  buildExportFileName,
  buildStudentsCsv,
  downloadCsvFile,
} from "../../../helpers/exportStudentsCsv";

export default function ExportCsvModal({
  show,
  onHide,
  currentQuery,
  visibleStudents,
  totalRecords,
}) {
  const [scope, setScope] = useState("filtered");
  const [selectedFields, setSelectedFields] = useState(DEFAULT_CSV_FIELDS);
  const [includeHeaders, setIncludeHeaders] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (show) {
      setScope("filtered");
      setSelectedFields(DEFAULT_CSV_FIELDS);
      setIncludeHeaders(true);
    }
  }, [show]);

  const hasActiveQuery = currentQuery && Object.values(currentQuery).some(
    (value) => typeof value === "string" && value.trim() !== ""
  );
  const visibleCount = Array.isArray(visibleStudents) ? visibleStudents.length : 0;

  const toggleField = (fieldKey) => {
    setSelectedFields((previous) =>
      previous.includes(fieldKey)
        ? previous.filter((key) => key !== fieldKey)
        : [...previous, fieldKey]
    );
  };

  const handleSelectAll = () => {
    setSelectedFields(STUDENT_CSV_FIELDS.map((field) => field.key));
  };

  const handleSelectDefaults = () => {
    setSelectedFields(DEFAULT_CSV_FIELDS);
  };

  async function fetchExportRows() {
    if (scope === "filtered" && !hasActiveQuery) {
      return Array.isArray(visibleStudents) ? visibleStudents : [];
    }
    const query = scope === "all" ? undefined : currentQuery;
    const pageSize = totalRecords && totalRecords > 0 ? totalRecords : 10000;
    const response = await getStudents({ page: 1, pageSize, query });
    return response.data || [];
  }

  const handleExport = async () => {
    if (selectedFields.length === 0) {
      notify.show("Selecciona al menos un campo para exportar", "warning");
      return;
    }
    setIsExporting(true);
    try {
      const rows = await fetchExportRows();
      if (!rows.length) {
        notify.show("No hay estudiantes para exportar", "warning");
        return;
      }
      const orderedFields = STUDENT_CSV_FIELDS.filter((field) =>
        selectedFields.includes(field.key)
      ).map((field) => field.key);
      const csvText = buildStudentsCsv(rows, orderedFields, includeHeaders);
      downloadCsvFile(buildExportFileName(), csvText);
      notify.show(`CSV exportado (${rows.length} estudiantes)`, "success");
      onHide();
    } catch (error) {
      notify.show(
        error?.response?.data?.message || "No se pudo exportar el CSV",
        "error"
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faFileCsv} className="mr-2 text-success" />
          Exportar estudiantes a CSV
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group>
          <Form.Label className="small font-weight-bold text-secondary">
            Alcance
          </Form.Label>
          <Form.Check
            custom
            type="radio"
            id="csv-scope-filtered"
            label={`Filtrados (${visibleCount} en pantalla / ${totalRecords || 0} total)`}
            checked={scope === "filtered"}
            onChange={() => setScope("filtered")}
          />
          <Form.Check
            custom
            type="radio"
            id="csv-scope-all"
            label="Todos los estudiantes"
            checked={scope === "all"}
            onChange={() => setScope("all")}
          />
          {!hasActiveQuery && scope === "filtered" && (
            <Alert variant="info" className="mt-2 mb-0 py-2 small">
              Sin filtros activos: se exportara la pagina visible actual.
            </Alert>
          )}
        </Form.Group>

        <Form.Group>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <Form.Label className="small font-weight-bold text-secondary mb-0">
              Datos a exportar
            </Form.Label>
            <div>
              <Button variant="link" size="sm" className="p-0 mr-2" onClick={handleSelectDefaults}>
                Por defecto
              </Button>
              <Button variant="link" size="sm" className="p-0" onClick={handleSelectAll}>
                Todos
              </Button>
            </div>
          </div>
          {STUDENT_CSV_FIELDS.map((field) => (
            <Form.Check
              custom
              key={field.key}
              type="checkbox"
              id={`csv-field-${field.key}`}
              label={field.label}
              checked={selectedFields.includes(field.key)}
              onChange={() => toggleField(field.key)}
            />
          ))}
        </Form.Group>

        <Form.Group className="mb-0">
          <Form.Check
            custom
            type="checkbox"
            id="csv-include-headers"
            label="Incluir encabezados en el CSV"
            checked={includeHeaders}
            onChange={(event) => setIncludeHeaders(event.target.checked)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isExporting}>
          Cancelar
        </Button>
        <Button
          variant="success"
          onClick={handleExport}
          disabled={isExporting || selectedFields.length === 0}
        >
          {isExporting ? (
            <>
              <Spinner animation="border" size="sm" className="mr-2" />
              Exportando...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faFileCsv} className="mr-2" />
              Exportar CSV
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
